import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { useUIStore } from '@/stores/uiStore';
import { clsx } from 'clsx';
import { NotificationCenter, ToastContainer } from '@/components/notifications';
import { useSignalRConnection, useUserNotifications, useLowStockAlerts } from '@/hooks/useSignalR';
import { useIsMobileNav } from '@/hooks/useMediaQuery';
import { useFailedNotificationAlerts } from '@/hooks/useFailedNotificationAlerts';

export function MainLayout() {
  const { sidebarOpen, sidebarCollapsed } = useUIStore();
  const isMobile = useIsMobileNav();

  // Initialize SignalR connection
  const { isConnected } = useSignalRConnection();

  // Listen for user-specific notifications
  useUserNotifications();

  // Listen for low-stock alerts (real-time, fires from Part.RemoveStock's domain event) - the
  // hook already pushes into the shared notification store/bell on its own, nothing further
  // needed here.
  useLowStockAlerts();

  useFailedNotificationAlerts();

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl" style={{ direction: 'rtl' }}>
      {/* Sidebar */}
      <Sidebar />

      {/* Main content - margin-right pushes content away from the right-side sidebar, but only on
          desktop. On mobile the sidebar is a drawer that overlays the page (see Sidebar.tsx) -
          pushing content here too would double-count the space and leave a squeezed sliver of
          page behind an opaque overlay. */}
      <div
        className="transition-all duration-300"
        dir="rtl"
        style={{
          direction: 'rtl',
          marginRight: isMobile
            ? '0'
            : sidebarOpen
              ? sidebarCollapsed
                ? '70px'
                : '260px'
              : '0',
          marginLeft: 0,
        }}
      >
        {/* Header */}
        <Header />

        {/* Page content */}
        <main className="p-4 sm:p-6">
          <Outlet />
        </main>
      </div>

      {/* Real-time notifications */}
      <NotificationCenter />
      <ToastContainer />

      {/* SignalR connection indicator (dev only) */}
      {import.meta.env.DEV && (
        <div
          className={clsx(
            'fixed bottom-4 left-4 px-2 py-1 rounded text-xs font-mono',
            isConnected ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          )}
        >
          SignalR: {isConnected ? 'Connected' : 'Disconnected'}
        </div>
      )}
    </div>
  );
}
